import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { MonthView, MonthViewHandle } from "../Calendar/MonthView";
import { YearView, YearViewHandle } from "./YearView";
import { DecadeView, DecadeViewHandle } from "./DecadeView";
import { PreviousNextSlider } from "../PreviousNextSlider/PreviousNextSlider";
import { never } from "client_legacy/ndaw/errors";
import styles from "./Calendar.module.less";
import { Button } from "../Button/Button";
import { localize } from "client_legacy/ndaw/culture";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import classNames from "classnames";
import { SliderWithToggle } from "../SliderWithToggle/SliderWithToggle";
import { FontIconName } from "../Icon/Icon";
import { useToday } from "client/Hooks";
import { Space } from "../Space/Space";
import { WeekView, WeekViewHandle } from "./WeekView";
import { TimeZone } from "client/DateAndTime/timezones";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import {
	convertToTimeOnly,
	convertToDateTime as convertTimeToDateTime
} from "client/DateAndTime/timeUtils";
import {
	addDays,
	addMonths,
	addYears,
	compareDates,
	convertToDateOnly,
	convertToDateTime,
	getStartOfWeek,
	yearsInADecade
} from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	DayOfWeek,
	getFirstWeekDay,
	getShortMonthNames
} from "client/DateAndTime/culture";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { FormattedText } from "../FormattedText/FormattedText";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { Key } from "client/Misc/commonTypes";
import { DateOnlyString } from "../DateString/DateOnlyString";
import { DateString } from "../DateString/DateString";

export type CalendarHandle = {
	focusDate(date: DateOnly): void;
};

type CalendarColor = "primary" | "app";

export type CalendarHighlightDateRange = {
	start: Date;
	end: Date;
};

export type CalendarProps = {
	value: Date | null;
	onChange(value: Date): void;
	showWeekNumbers?: boolean;
	color?: CalendarColor;
	eventDates?: readonly Date[];
	sliderAction?: SliderActionOptions | undefined;
	shouldHideToday?: boolean;
	trackingId: string | undefined;
	view: CalendarView;
	onViewChange: (view: CalendarView) => void;
	timezone: TimeZone;
	getCustomHighlightRange?(date: Date): CalendarHighlightDateRange;
	minDate?: Date;
	maxDate?: Date;
	ref?: Ref<CalendarHandle>;
};

export type CalendarView = {
	date: Date;
	viewKind: CalendarViewKind;
};

export const Calendar = (props: CalendarProps): ReactElement => {
	const {
		value,
		onChange,
		showWeekNumbers,
		color = "primary",
		eventDates,
		sliderAction,
		shouldHideToday = false,
		trackingId,
		view,
		onViewChange,
		timezone,
		getCustomHighlightRange,
		minDate,
		maxDate,
		ref
	} = props;

	const [selectedDateIndicatorId] = useState<Guid>(() => newGuid());

	const today: DateOnly = useConvertToDateOnly(
		useToday({ timezone }),
		timezone
	);
	const viewDate: DateOnly = useConvertToDateOnly(view.date, timezone);
	const valueDate: DateOnly | null = useConvertToDateOnly(value, timezone);
	const minDateOnly: DateOnly | null = useConvertToDateOnly(minDate ?? null, timezone);
	const maxDateOnly: DateOnly | null = useConvertToDateOnly(maxDate ?? null, timezone);

	const effectiveMaxDateOnly: DateOnly | null = useMemo(
		(): DateOnly | null => {
			if (
				isNotNil(minDateOnly)
					&& isNotNil(maxDateOnly)
					&& compareDates(maxDateOnly, minDateOnly) === -1
			) {
				console.warn("Calendar: maxDate is before minDate; ignoring maxDate.");

				return null;
			}

			return maxDateOnly;
		},
		[minDateOnly, maxDateOnly]
	);

	const viewRef: RefObject<ViewHandle | null> = useRef<ViewHandle>(null);

	useImperativeHandle(
		ref,
		(): CalendarHandle => ({
			focusDate: (date: DateOnly): void => {
				// the setTimeout is because the view might be not mounted yet
				setTimeout(
					(): void => {
						viewRef.current
							?.focusDate(date);
					},
					1
				);
			}
		}),
		[]
	);

	const calculateHighlightRangeForDate = useCallback(
		(date: DateOnly): CalendarHighlightDateOnlyRange => {
			if (!getCustomHighlightRange) {
				return {
					start: date,
					end: addDays(date, 1)
				};
			}

			const customDateTimeRange: CalendarHighlightDateRange = getCustomHighlightRange(
				convertToDateTime(date, timezone)
			);

			return {
				start: convertToDateOnly(customDateTimeRange.start, timezone),
				end: convertToDateOnly(customDateTimeRange.end, timezone)
			};
		},
		[getCustomHighlightRange, timezone]
	);

	const highlightRange = useMemo(
		(): CalendarHighlightDateOnlyRange | null => {
			if (!valueDate) {
				return null;
			}

			return calculateHighlightRangeForDate(valueDate);
		},
		[
			calculateHighlightRangeForDate,
			valueDate
		]
	);

	const changeViewDateHandler = useCallback(
		(date: DateOnly): void => {
			onViewChange({
				...view,
				date: convertToDateTime(date, timezone)
			});
		},
		[
			onViewChange,
			view,
			timezone
		]
	);

	const upRange = useCallback(
		(): void => {
			onViewChange({
				...view,
				viewKind: view.viewKind - 1
			});
		},
		[onViewChange, view]
	);

	const isTodaySelectable = useMemo(
		(): boolean => isCalendarDateSelectable(
			today,
			minDateOnly,
			effectiveMaxDateOnly
		),
		[
			effectiveMaxDateOnly,
			minDateOnly,
			today
		]
	);

	const todayHandler = useCallback(
		(): void => {
			let timeValue: TimeOnly;

			if (isNotNil(value)) {
				timeValue = convertToTimeOnly(value, timezone);
			} else {
				timeValue = new TimeOnly(0, 0, 0, 0);
			}

			const newValue: Date = convertTimeToDateTime(
				timeValue,
				timezone,
				new Date()
			);

			onChange(newValue);
		},
		[
			onChange,
			value,
			timezone
		]
	);

	const eventDateMap = useMemo(
		(): Map<DateOnly, boolean> => {
			const result = new NormalizedMap<DateOnly, boolean>(
				(x) => x.toISOString()
			);

			if (eventDates) {
				for (const date of eventDates) {
					result.set(convertToDateOnly(date, timezone), true);
				}
			}

			return result;
		},
		[eventDates, timezone]
	);

	const changeDateHandler = useCallback(
		(date: DateOnly): void => {
			onChange(convertToDateTime(date, timezone));
		},
		[onChange, timezone]
	);

	const firstWeekDay: DayOfWeek = getFirstWeekDay();

	const changeViewRangeHandler = useCallback(
		(modifier: 1 | -1): void => {
			changeViewDateHandler(
				calculateAdjustedDate(
					viewDate,
					view.viewKind,
					modifier
				)
			);
		},
		[
			changeViewDateHandler,
			view.viewKind,
			viewDate
		]
	);

	return (
		<div
			className={classNames(
				styles.root,
				colorToStyleMapping[color]
			)}
			role="group"
			aria-labelledby={selectedDateIndicatorId}
		>
			<Slider
				view={view.viewKind}
				date={viewDate}
				onRangeChange={changeViewRangeHandler}
				onToday={!shouldHideToday
					? todayHandler
					: undefined
				}
				isTodayDisabled={!isTodaySelectable}
				onUpRange={upRange}
				trackingId={generateTrackingId(trackingId, "__slider")}
				action={sliderAction}
				timezone={timezone}
			/>

			{view.viewKind === CalendarViewKind.Decade && (
				<DecadeView
					decade={getDecade(viewDate)}
					minYear={minDateOnly?.year}
					maxYear={effectiveMaxDateOnly?.year}
					today={today}
					highlightedDate={valueDate}
					onSelect={(year) => {
						const newDate: DateOnly = new DateOnly(
							year,
							0,
							1
						);

						onViewChange({
							viewKind: CalendarViewKind.Year,
							date: convertToDateTime(newDate, timezone)
						});

						// the setTimeout is because the view isn't mounted yet
						setTimeout(
							(): void => {
								viewRef.current
									?.focusDate(newDate);
							},
							1
						);


					}}
					onViewRangeChange={changeViewRangeHandler}
					ref={viewRef}
				/>
			)}

			{view.viewKind === CalendarViewKind.Year && (
				<YearView
					year={viewDate.year}
					minDate={minDateOnly}
					maxDate={effectiveMaxDateOnly}
					today={today}
					highlightedDate={valueDate}
					onSelect={(month) => {
						const newDate: DateOnly = new DateOnly(
							viewDate.year,
							month,
							1
						);

						onViewChange({
							viewKind: CalendarViewKind.Month,
							date: convertToDateTime(newDate, timezone)
						});

						// the setTimeout is because the view isn't mounted yet
						setTimeout(
							(): void => {
								viewRef.current
									?.focusDate(newDate);
							},
							1
						);

					}}
					onViewRangeChange={changeViewRangeHandler}
					ref={viewRef}
				/>
			)}

			{view.viewKind === CalendarViewKind.Month && (
				<MonthView
					firstWeekDay={firstWeekDay}
					year={viewDate.year}
					month={viewDate.monthIndex}
					today={today}
					highlightRange={highlightRange}
					showWeekNumbers={showWeekNumbers}
					onSelect={changeDateHandler}
					eventDateMap={eventDateMap}
					calculateHighlightRangeForDate={calculateHighlightRangeForDate}
					onViewRangeChange={changeViewRangeHandler}
					minDate={minDateOnly}
					maxDate={effectiveMaxDateOnly}
					ref={viewRef}
				/>
			)}

			{view.viewKind === CalendarViewKind.WeekView && (
				<WeekView
					firstWeekDay={firstWeekDay}
					minDate={minDateOnly}
					maxDate={effectiveMaxDateOnly}
					viewDate={viewDate}
					today={today}
					highlightRange={highlightRange}
					showWeekNumbers={showWeekNumbers}
					onSelect={changeDateHandler}
					eventDateMap={eventDateMap}
					calculateHighlightRangeForDate={calculateHighlightRangeForDate}
					onViewRangeChange={changeViewRangeHandler}
					ref={viewRef}
				/>
			)}

			{isNotNil(valueDate) && (
				<div
					className={styles.visuallyHidden}
					aria-live="polite"
					aria-atomic="true"
					id={selectedDateIndicatorId}
				>
					<FormattedText
						text={localize("_SelectedDateX")}
						thingsToReplace={[
							{
								what: "{{Date}}",
								filler: (
									<DateOnlyString
										format="DMY"
										value={valueDate}
									/>
								)
							}
						]}
					/>
				</div>
			)}
		</div>
	);
};
Calendar.displayName = "Calendar";

export type CalendarHighlightDateOnlyRange = {
	start: DateOnly;
	end: DateOnly;
};

function useConvertToDateOnly(date: Date, timezone: TimeZone): DateOnly;
function useConvertToDateOnly(date: Date | null, timezone: TimeZone): DateOnly | null;
function useConvertToDateOnly(date: Date | null, timezone: TimeZone): DateOnly | null {
	return useMemo(
		(): DateOnly | null => {
			return isNotNil(date)
				? convertToDateOnly(date, timezone)
				: null;
		},
		[date, timezone]
	);
}

const colorToStyleMapping: { [key in CalendarColor]: string; } = {
	["primary"]: styles.root_primary,
	["app"]: styles.root_app
};

export type SliderActionOptions = {
	icon: FontIconName;
	onExecute: () => void;
};

type SliderProps = {
	view: CalendarViewKind;
	date: DateOnly;
	onRangeChange: (modifier: 1 | -1) => void;
	onUpRange: () => void;
	onToday?: () => void;
	isTodayDisabled?: boolean;
	action: SliderActionOptions | undefined;
	trackingId: string | undefined;
	timezone: TimeZone;
};

const Slider = (props: SliderProps): ReactElement => {
	const {
		view,
		date,
		onRangeChange,
		onUpRange,
		onToday,
		isTodayDisabled,
		action,
		trackingId,
		timezone
	} = props;

	return (
		<div
			className={styles.slider}
			{...setTrackingId(trackingId)}
		>
			<SliderWithToggle>
				<SliderWithToggle.Slider>
					<div className={styles.sliderContent}>
						{onToday && (
							<Button
								kind="secondary"
								text={localize("_Today")}
								onClick={onToday}
								isDisabled={isTodayDisabled}
								trackingId={generateTrackingId(trackingId, "__today")}
							/>
						)}

						<div className={styles.sliderPreviousNext}>
							<PreviousNextSlider
								onPrevious={() => onRangeChange(-1)}
								onNext={() => onRangeChange(1)}
								onLabel={view === CalendarViewKind.Decade ? undefined : onUpRange}
							>
								<SliderTitle
									view={view}
									date={date}
									timezone={timezone}
								/>
							</PreviousNextSlider>
						</div>
					</div>
				</SliderWithToggle.Slider>

				{action && (
					<SliderWithToggle.Toggle
						icon={action.icon}
						onClick={action.onExecute}
					/>
				)}
			</SliderWithToggle>
		</div>
	);
};
Slider.displayName = "Slider";

type SliderTitleProps = {
	view: CalendarViewKind;
	date: DateOnly;
	timezone: TimeZone;
};

const SliderTitle = (props: SliderTitleProps): ReactElement => {
	const {
		view,
		date,
		timezone
	} = props;

	const year: number = date.year;

	const renderContent = (): ReactElement => {
		switch (view) {
			case CalendarViewKind.Decade:
				const decadeStart: number = Math.floor(year / yearsInADecade) * yearsInADecade;
				return <>{decadeStart} - {decadeStart + (yearsInADecade - 1)}</>;
			case CalendarViewKind.Year:
				return <>{year}</>;
			case CalendarViewKind.Month:
				return <>{getShortMonthNames()[date.monthIndex]} {year}</>;
			case CalendarViewKind.WeekView:
				const firstDayOfWeek: Date = convertToDateTime(getStartOfWeek(date), timezone);
				const lastDayOfWeek: Date = convertToDateTime(
					addDays(
						getStartOfWeek(date),
						6
					),
					timezone
				);

				return (
					<>
						<DateString
							format="DM(Y)"
							value={firstDayOfWeek}
							timezone={timezone}
						/>
						<Space />
						-
						<Space />
						<DateString
							format="DM(Y)"
							value={lastDayOfWeek}
							timezone={timezone}
						/>
					</>
				);
			default:
				never(view);
		}
	};

	return (
		<>
			<span
				{...setTrackingId("slider-title")}
				aria-live="polite"
				aria-atomic="true"
				role="status"
			>
				{renderContent()}
			</span>
			{view !== CalendarViewKind.Decade && (
				<span className={styles.visuallyHidden}>
					<FormattedText
						text={localize("_SelectToChangeToX")}
						thingsToReplace={[
							{
								what: "{{Kind}}",
								filler: localize(calendarViewKindToChangeViewLabelMap[view])
							}
						]}
					/>
				</span>
			)}
		</>
	);
};

SliderTitle.displayName = "SliderTitle";

export const enum CalendarViewKind {
	Decade = 0,
	Year = 1,
	Month = 2,
	WeekView = 3
}

export const calendarViewKindToChangeViewLabelMap: {
	[key in Exclude<CalendarViewKind, CalendarViewKind.Decade>]: LocalizationKey;
} = {
	[CalendarViewKind.WeekView]: "_Days",
	[CalendarViewKind.Month]: "_Month",
	[CalendarViewKind.Year]: "_Year"
};

const YEARS_IN_DECADE = 10;

type CalendarViewAndRange = {
	view: CalendarView;
	onViewChange: (view: CalendarView) => void;
	viewRange: [from: Date, to: Date];
};

export function useCalendar(
	initialViewKind: CalendarViewKind,
	viewDate: Date | nil,
	timezone: TimeZone
): CalendarViewAndRange {
	const [state, setState] = useState<CalendarView>(() => {
		return {
			viewKind: initialViewKind,
			date: viewDate ?? new Date()
		};
	});

	const viewRange = useMemo(
		(): [from: Date, to: Date] => {
			const { date, viewKind: view } = state;
			const dateOnly: DateOnly = convertToDateOnly(date, timezone);
			const year: number = dateOnly.year;
			const month: number = dateOnly.monthIndex;

			switch (view) {
				case CalendarViewKind.WeekView:
					const startOfWeek: DateOnly = getStartOfWeek(dateOnly);

					const endOfWeek: DateOnly = addDays(
						startOfWeek,
						7
					);

					return [
						convertToDateTime(startOfWeek, timezone),
						convertToDateTime(endOfWeek, timezone)
					];
				case CalendarViewKind.Month:
					return [
						convertToDateTime(new DateOnly(year, month, 1), timezone),
						convertToDateTime(
							addMonths(
								new DateOnly(year, month, 1),
								1
							),
							timezone
						)
					];
				case CalendarViewKind.Year:
					return [
						convertToDateTime(new DateOnly(year, 0, 1), timezone),
						convertToDateTime(
							addYears(
								new DateOnly(year, 0, 1),
								1
							),
							timezone
						)
					];

				case CalendarViewKind.Decade:
					{
						const decade: number = getDecade(dateOnly);
						const startYear: number = decade * YEARS_IN_DECADE;

						return [
							convertToDateTime(new DateOnly(startYear, 0, 1), timezone),
							convertToDateTime(
								addYears(
									new DateOnly(startYear, 0, 1),
									yearsInADecade
								),
								timezone
							)
						];
					}
				default:
					never(view);
			}
		},
		[state, timezone]
	);

	return {
		view: state,
		onViewChange: setState,
		viewRange
	};
}

function calculateAdjustedDate(
	date: DateOnly,
	unit: CalendarViewKind,
	modifier: number
): DateOnly {
	switch (unit) {
		case CalendarViewKind.Decade:
			return addYears(
				date,
				10 * modifier
			);
		case CalendarViewKind.Year:
			return addYears(
				date,
				modifier
			);
		case CalendarViewKind.Month:
			return addMonths(
				date,
				modifier
			);
		case CalendarViewKind.WeekView:
			return addDays(
				date,
				7 * modifier
			);
		default:
			never(unit);
	}
}

function getDecade(date: DateOnly): number {
	const year: number = date.year;
	return Math.floor(year / YEARS_IN_DECADE);
}

export function isCalendarDateSelectable(
	date: DateOnly,
	minDate: DateOnly | null,
	maxDate: DateOnly | null
): boolean {
	if (isNotNil(minDate) && compareDates(date, minDate) === -1) {
		return false;
	}

	if (isNotNil(maxDate) && compareDates(date, maxDate) === 1) {
		return false;
	}

	return true;
}

const calendarNavigationKeys = [
	Key.ArrowUp,
	Key.ArrowDown,
	Key.ArrowLeft,
	Key.ArrowRight
] as const;

export type CalendarNavigationKey = typeof calendarNavigationKeys[number];

export function isCalendarNavigationKey(key: Key): key is CalendarNavigationKey {
	return calendarNavigationKeys.includes(key as CalendarNavigationKey);
}

type ViewHandle = DecadeViewHandle
	| YearViewHandle
	| MonthViewHandle
	| WeekViewHandle;